<script setup>
import { computed, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  activeList: { type: String, default: 'rascunhos' },
  inline: { type: Boolean, default: false },
  sort: { type: String, default: '' },
});

const emit = defineEmits(['change']);
const { t } = useI18n();
const open = ref(false);
const menu = ref(null);
const menuId = `sales-sort-${useId()}`;

const dateDirections = computed(() => [
  { id: 1, label: t('SALES.SORT.RECENT') },
  { id: 2, label: t('SALES.SORT.OLDEST') },
]);
const valueDirections = computed(() => [
  { id: 1, label: t('SALES.SORT.HIGHEST') },
  { id: 2, label: t('SALES.SORT.LOWEST') },
]);
const progressDirections = computed(() => [
  { id: 1, label: t('SALES.SORT.MOST_PROGRESS') },
  { id: 2, label: t('SALES.SORT.LEAST_PROGRESS') },
]);

const groups = computed(() => {
  if (props.activeList === 'andamento') {
    return [
      {
        patch: 'data_entrada',
        label: t('SALES.SORT.ENTRY_DATE'),
        directions: dateDirections.value,
      },
      {
        patch: 'order_status',
        label: t('SALES.SORT.STATUS_DEADLINE'),
        directions: valueDirections.value,
      },
    ];
  }

  if (props.activeList === 'rascunhos') {
    return [
      {
        patch: 'order_rank',
        label: t('SALES.SORT.PROGRESS_OR_RANK'),
        directions: progressDirections.value,
      },
    ];
  }

  if (props.activeList === 'revisoes') {
    return [
      {
        patch: 'order_assinatura',
        label: t('SALES.SORT.SIGNATURE_DATE'),
        directions: dateDirections.value,
      },
      {
        patch: 'data_pedido',
        label: t('SALES.SORT.REQUEST_DATE'),
        directions: dateDirections.value,
      },
      {
        patch: 'dias_corridos',
        label: t('SALES.SORT.ELAPSED_DAYS'),
        directions: [
          { id: 1, label: t('SALES.SORT.MORE_DAYS') },
          { id: 2, label: t('SALES.SORT.FEWER_DAYS') },
        ],
      },
    ];
  }

  if (props.activeList === 'entregues') {
    return [
      {
        patch: 'order_assinatura',
        label: t('SALES.SORT.SIGNATURE_DATE'),
        directions: dateDirections.value,
      },
      {
        patch: 'order_escritura',
        label: t('SALES.SORT.WRITING_DEADLINE'),
        directions: valueDirections.value,
      },
      {
        patch: 'order_status',
        label: t('SALES.SORT.STATUS_DEADLINE'),
        directions: valueDirections.value,
      },
    ];
  }

  return [];
});

const isSelected = (patch, id) => props.sort === `${patch}_${id}`;
const close = () => {
  menu.value?.hidePopover?.();
  open.value = false;
};

const select = (patch, id) => {
  emit('change', `${patch}_${id}`);
  close();
};
const clear = () => {
  emit('change', '');
  close();
};

const syncPopover = event => {
  open.value = event.newState === 'open';
};

watch(() => props.activeList, close);
</script>

<template>
  <div class="contents">
    <div v-if="groups.length" class="relative">
      <button
        type="button"
        class="inline-flex min-h-10 items-center gap-2 text-sm font-bold text-lais-text hover:bg-lais-bg-soft"
        :class="
          inline
            ? 'rounded-[10px] px-2'
            : 'rounded-lais border border-lais-border bg-white px-3'
        "
        :aria-expanded="open"
        :aria-label="t('SALES.ACTIONS.ORDER')"
        :popovertarget="menuId"
      >
        <span
          class="i-lucide-arrow-down-wide-narrow text-base text-lais-primary"
        />
        {{ t('SALES.ACTIONS.ORDER') }}
        <span v-if="sort" class="text-lais-primary">
          {{ t('SALES.ACTIONS.ORDER_COUNT', { count: 1 }) }}
        </span>
      </button>
      <section
        :id="menuId"
        ref="menu"
        popover="auto"
        class="inset-auto m-0 w-[calc(100vw-2rem)] max-w-md rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card [position-area:block-end_center] sm:[position-area:block-end_span-inline-end]"
        :aria-label="t('SALES.ACTIONS.ORDER')"
        @toggle="syncPopover"
      >
        <fieldset
          v-for="group in groups"
          :key="group.patch"
          class="mb-4 last:mb-0"
        >
          <legend class="mb-2 text-sm font-bold text-lais-text">
            {{ group.label }}
          </legend>
          <label
            v-for="direction in group.directions"
            :key="`${group.patch}-${direction.id}`"
            class="flex cursor-pointer items-center gap-2 rounded-lais px-2 py-1.5 text-sm text-lais-text-body hover:bg-lais-bg-soft"
          >
            <input
              type="radio"
              name="sales-order"
              :checked="isSelected(group.patch, direction.id)"
              @change="select(group.patch, direction.id)"
            />
            {{ direction.label }}
          </label>
        </fieldset>
        <button
          type="button"
          class="mt-2 rounded-lais px-3 py-2 text-sm font-semibold text-lais-primary hover:bg-lais-primary-50"
          @click="clear"
        >
          {{ t('SALES.ACTIONS.CLEAR_ORDER') }}
        </button>
      </section>
    </div>
  </div>
</template>
